import { ref } from 'vue'

import { message } from 'ant-design-vue'

import type { RouteLocationNormalizedLoaded } from 'vue-router'

import starApi from '@/api/starApi'



/** 权益激活二维码占位地址 */

export function getStarActivationQrUrl(): string {

  return 'https://s.techgp.cn/9L2PTJ'

}



function resolveAuth(route: RouteLocationNormalizedLoaded, userName?: string) {

  const token = route.query.token as string

  const name =

    userName ||

    (route.query.username as string) ||

    (route.query.userName as string) ||

    ''

  return { token, userName: name }

}



type QueryResult =

  | { success: true; hasUnactivatedOrder: boolean }

  | { success: false; errorMessage: string }



function isQuerySuccess(res: any): boolean {

  if (!res || typeof res !== 'object') return false

  if (res.code != null) return Number(res.code) === 1

  if (res.status != null) return Number(res.status) === 1

  return Array.isArray(res.data)

}



function resolveQueryError(res: any): string {

  return res?.message || res?.msg || res?.toast || '查询激活状态失败，请稍后重试'

}



function resolveRequestError(e: unknown): string {
  const msg = (e as any)?.message as string | undefined
  if (msg && /timeout|timed out/i.test(msg)) return '查询激活状态超时，请稍后重试'
  return '查询激活状态失败，请稍后重试'
}

function getQueryKey(route: RouteLocationNormalizedLoaded, userName?: string) {
  const { token, userName: name } = resolveAuth(route, userName)
  return `${token}|${name}`
}

/** 按用户缓存 getStrategyList 返回的 testNumRemain，避免每次操作重复请求 */
const trialRemainCache = new Map<string, number>()

/** 首页/列表拉取后写入缓存 */
export function updateStarTrialRemainCache(
  route: RouteLocationNormalizedLoaded,
  userName: string | undefined,
  testNumRemain: unknown
) {
  if (typeof testNumRemain !== 'number') return
  trialRemainCache.set(getQueryKey(route, userName), testNumRemain)
}

function getCachedTrialRemain(
  route: RouteLocationNormalizedLoaded,
  userName?: string
): boolean | null {
  const cached = trialRemainCache.get(getQueryKey(route, userName))
  if (cached === undefined) return null
  return cached > 0
}

/**
 * 查询策略列表剩余试用次数（仅缓存未命中时请求接口）。
 * 返回 true 表示 testNumRemain > 0；false 表示无剩余次数；null 表示查询失败。
 */
async function queryTrialRemain(
  route: RouteLocationNormalizedLoaded,
  userName?: string
): Promise<boolean | null> {
  const cached = getCachedTrialRemain(route, userName)
  if (cached !== null) return cached

  const { token, userName: name } = resolveAuth(route, userName)
  if (!token || !name) return null

  try {
    const res = await starApi.getStrategyList({
      data: { from: 'all' },
      headers: { token, userName: name },
    })
    const testNumRemain = res?.data?.testNumRemain
    if (typeof testNumRemain === 'number') {
      updateStarTrialRemainCache(route, userName, testNumRemain)
      return testNumRemain > 0
    }
    return false
  } catch (e) {
    console.error('[queryTrialRemain]', e)
    return null
  }
}

let pendingTrialQuery: Promise<boolean | null> | null = null
let pendingTrialQueryKey = ''

async function queryTrialRemainWithDedupe(
  route: RouteLocationNormalizedLoaded,
  userName?: string
): Promise<boolean | null> {
  const key = getQueryKey(route, userName)
  if (pendingTrialQuery && pendingTrialQueryKey === key) {
    return pendingTrialQuery
  }
  pendingTrialQueryKey = key
  pendingTrialQuery = queryTrialRemain(route, userName).finally(() => {
    pendingTrialQuery = null
    pendingTrialQueryKey = ''
  })
  return pendingTrialQuery
}

/**

 * 查询是否存在未激活的星投订单。

 * 仅当接口成功且 data 为非空数组时，hasUnactivatedOrder 为 true。

 */

export async function queryStarActivationStatus(

  route: RouteLocationNormalizedLoaded,

  userName?: string

): Promise<QueryResult> {

  const { token, userName: name } = resolveAuth(route, userName)

  if (!token || !name) return { success: true, hasUnactivatedOrder: false }



  try {

    const res = await starApi.queryUnactivatedStarOrder({

      headers: { token, userName: name, moduleCode: 'quantization' },

    })

    if (!isQuerySuccess(res)) {

      return { success: false, errorMessage: resolveQueryError(res) }

    }

    const list = res.data

    if (!Array.isArray(list) || list.length === 0) {

      return { success: true, hasUnactivatedOrder: false }

    }

    return { success: true, hasUnactivatedOrder: true }

  } catch (e) {

    console.error('[queryStarActivationStatus]', e)

    return { success: false, errorMessage: resolveRequestError(e) }

  }

}



const showActivationDialog = ref(false)

const showActivationQr = ref(false)



let pendingQuery: Promise<QueryResult> | null = null

let pendingQueryKey = ''

async function queryWithDedupe(

  route: RouteLocationNormalizedLoaded,

  userName?: string

): Promise<QueryResult> {

  const key = getQueryKey(route, userName)

  if (pendingQuery && pendingQueryKey === key) {

    return pendingQuery

  }

  pendingQueryKey = key

  pendingQuery = queryStarActivationStatus(route, userName).finally(() => {

    pendingQuery = null

    pendingQueryKey = ''

  })

  return pendingQuery

}



/** 打开权益激活弹框（模块级，供 useStarTip 等复用） */
export function openStarActivationDialog() {
  showActivationQr.value = false
  showActivationDialog.value = true
}

/** 带去重的待激活订单查询 */
export async function queryStarActivationWithDedupe(
  route: RouteLocationNormalizedLoaded,
  userName?: string
): Promise<QueryResult> {
  return queryWithDedupe(route, userName)
}

export function useStarActivation() {

  const showActivation = () => {
    openStarActivationDialog()
  }



  const hideActivation = () => {

    showActivationDialog.value = false

    showActivationQr.value = false

  }



  /** 仅查激活状态：未激活时弹窗并返回 false；接口失败时提示错误并返回 false */
  async function ensureStarActivationChecked(
    route: RouteLocationNormalizedLoaded,
    userName?: string
  ): Promise<boolean> {
    const result = await queryWithDedupe(route, userName)

    if (!result.success) {
      hideActivation()
      message.error(result.errorMessage)
      return false
    }

    if (result.hasUnactivatedOrder) {
      showActivation()
      return false
    }

    hideActivation()
    return true
  }

  /**
   * 先判断 testNumRemain > 0；否则再查激活状态。
   * 未激活时弹出提醒并返回 false；接口失败时提示错误并返回 false。
   */
  async function ensureStarActivated(
    route: RouteLocationNormalizedLoaded,
    userName?: string
  ): Promise<boolean> {
    const hasTrialRemain = await queryTrialRemainWithDedupe(route, userName)

    if (hasTrialRemain === true) {
      hideActivation()
      return true
    }

    return ensureStarActivationChecked(route, userName)
  }



  const openActivationQr = () => {

    showActivationQr.value = true

  }



  const refreshActivation = async (

    route: RouteLocationNormalizedLoaded,

    userName?: string

  ) => {

    const result = await queryWithDedupe(route, userName)

    if (!result.success) {

      message.error(result.errorMessage)

      return

    }

    if (!result.hasUnactivatedOrder) {

      hideActivation()

    }

  }



  return {

    showActivationDialog,

    showActivationQr,

    ensureStarActivated,

    ensureStarActivationChecked,

    openActivationQr,

    refreshActivation,

    hideActivation,

  }

}


